import { z } from 'zod';

import type { AnimalTypeLabel } from '@/services/animal-management';
import type { CreateAnimalInput } from '@/services/animal-management/api';
import { ORIGIN_STATE_CODES } from '@/services/animal-management/origin';

const baseSchema = z.object({
  animalType: z.custom<AnimalTypeLabel>(
    (val) => ['Donor', 'Recipient', 'Calf'].includes(val as string),
    { message: 'Required' },
  ),
  nameTag: z.string(),
  name: z.string(),
  tag: z.string(),
  breedId: z.string(),
  rfidTag: z.string(),
  birthDate: z.date().optional(),
  ownerId: z.string(),
  yardId: z.string(),
  receivedDate: z.date().optional(),
  weight: z.number().int('Enter a whole number').min(0).optional(),
  penId: z.string(),
  registrationNumber: z.string().optional(),
  shipDate: z.date().optional(),
  opuProgram: z.string().optional(),
  togId: z.string().optional(),
  programCategory: z.string().optional(),
  pregnant: z.enum(['Yes', 'No']).optional(),
  ivfEtStatus: z.string().optional(),
  origin: z
    .string()
    .refine(
      (v) => !v || (ORIGIN_STATE_CODES as readonly string[]).includes(v),
      'Enter a US state code',
    ),
  hairSample: z.enum(['Yes', 'No', '']).optional(),
  heiferCow: z.string().optional(),
  lastDrugUsedId: z.string().optional(),
  client: z.string().optional(),
  colorId: z.string().optional(),
  ocvStatusId: z.string().optional(),
  recipientStatusId: z.string().optional(),
  sex: z.string().optional(),
});

export type AddAnimalFormValues = z.infer<typeof baseSchema>;

type AddField = Exclude<
  keyof AddAnimalFormValues,
  'animalType' | 'togId' | 'ivfEtStatus'
>;

// #35316 field classification ("FINAL Dynamics Inventory_Animals - Field
// Classification 7.14.26.xlsx"), limited to fields the legacy table can store
// (see the Writes section of the API's field-mapping.md). Required fields must
// be filled; Visible fields show but are optional; everything else, including
// the "Not Required" columns, is hidden and never sent. LOPU Donor has no
// legacy table yet and cannot be created.
const REQUIRED_FIELDS: Record<AnimalTypeLabel, ReadonlySet<AddField>> = {
  Donor: new Set([
    'nameTag',
    'ownerId',
    'yardId',
    'origin',
    'receivedDate',
    'breedId',
  ]),
  Recipient: new Set([
    'tag',
    'rfidTag',
    'breedId',
    'colorId',
    'recipientStatusId',
    'ocvStatusId',
    'client',
    'ownerId',
    'origin',
    'receivedDate',
    'yardId',
  ]),
  Calf: new Set([
    'ownerId',
    'tag',
    'breedId',
    'yardId',
    'penId',
    'origin',
    'sex',
  ]),
  'LOPU Donor': new Set(),
};

const OPTIONAL_FIELDS: Record<AnimalTypeLabel, ReadonlySet<AddField>> = {
  Donor: new Set([
    'name',
    'rfidTag',
    'birthDate',
    'registrationNumber',
    'shipDate',
    'heiferCow',
    'hairSample',
    'ocvStatusId',
    'opuProgram',
    'programCategory',
    'pregnant',
  ]),
  Recipient: new Set(['shipDate', 'weight', 'lastDrugUsedId']),
  Calf: new Set([
    'name',
    'rfidTag',
    'birthDate',
    'registrationNumber',
    'shipDate',
    'weight',
    'colorId',
    'opuProgram',
  ]),
  'LOPU Donor': new Set(),
};

export const isAddFieldVisible = (
  type: AnimalTypeLabel | undefined,
  field: AddField,
) =>
  Boolean(type) &&
  (REQUIRED_FIELDS[type!].has(field) || OPTIONAL_FIELDS[type!].has(field));

export const isAddFieldRequired = (
  type: AnimalTypeLabel | undefined,
  field: AddField,
) => Boolean(type) && REQUIRED_FIELDS[type!].has(field);

/**
 * Picklists whose options could not be loaded cannot be filled in, so they are
 * not enforced rather than blocking every create.
 */
export type AddSchemaOptions = { unavailable?: ReadonlySet<AddField> };

export const createAddAnimalSchema = ({ unavailable }: AddSchemaOptions = {}) =>
  baseSchema.superRefine((data, ctx) => {
    if (!data.animalType) return;
    for (const field of REQUIRED_FIELDS[data.animalType]) {
      if (unavailable?.has(field)) continue;
      const value = data[field];
      if (value === undefined || value === null || value === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: [field],
        });
      }
    }
  });

export const addAnimalSchema = createAddAnimalSchema();

export const ADD_ANIMAL_TYPE_CODES: Record<AnimalTypeLabel, number> = {
  Donor: 1,
  'LOPU Donor': 2,
  Recipient: 3,
  Calf: 4,
};

export function buildCreateAnimalInput(
  data: AddAnimalFormValues,
): CreateAnimalInput {
  const type = data.animalType;
  const visible = (field: AddField) => isAddFieldVisible(type, field);
  const text = (field: AddField) =>
    visible(field) ? (data[field] as string | undefined)?.trim() || null : null;
  const date = (field: 'birthDate' | 'receivedDate' | 'shipDate') =>
    visible(field) && data[field] ? data[field].toISOString() : null;
  const int = (field: 'client' | 'heiferCow' | 'sex') =>
    visible(field) && data[field] ? Number(data[field]) : null;

  return {
    animalType: ADD_ANIMAL_TYPE_CODES[type],
    // Donor: tog_name from Name/Tag, pedigree name from Name. Calf has a single
    // name column (tog_pedigreename), fed from Name.
    name: type === 'Calf' ? text('name') : text('nameTag'),
    pedigreeName: type === 'Donor' ? text('name') : null,
    tag: text('tag'),
    metalTag: text('rfidTag'),
    registration: text('registrationNumber'),
    breedId: text('breedId'),
    ownerId: text('ownerId'),
    yardId: text('yardId'),
    penId: text('penId'),
    birth: date('birthDate'),
    weight: visible('weight') && data.weight ? data.weight : null,
    programCategory: text('programCategory'),
    opuProgram: text('opuProgram'),
    pregnant:
      visible('pregnant') && data.pregnant
        ? data.pregnant === 'Yes'
          ? 1
          : 0
        : null,
    received: date('receivedDate'),
    shipOn: date('shipDate'),
    origin: text('origin'),
    hairSample:
      visible('hairSample') && data.hairSample
        ? data.hairSample === 'Yes'
        : null,
    heiferCow: int('heiferCow'),
    lastDrugUsedId: text('lastDrugUsedId'),
    client: int('client'),
    colorId: text('colorId'),
    ocvStatusId: text('ocvStatusId'),
    recipientStatusId: text('recipientStatusId'),
    sex: int('sex'),
  };
}
